import { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X, Send, Loader2, Minus, Shield } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useAuthModal } from "@/hooks/useAuthModal";
import { getUserAccessContext } from "@/lib/access";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import AdminChatView from "@/components/AdminChatView";

interface SupportMessage {
  id: string;
  message: string;
  sender_type: "user" | "team";
  is_read: boolean;
  created_at: string;
}

const WELCOME_MSG = "👋 Bonjour ! Comment pouvons-nous vous aider ? Notre équipe vous répondra dès que possible.";
const RATE_LIMIT_MS = 2000;

const SupportChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [adminMode, setAdminMode] = useState<"support" | "conversations">("conversations");
  const lastSentRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const { openLogin } = useAuthModal();
  const location = useLocation();

  const isAdmin = location.pathname.startsWith("/admin");

  // Check super admin status
  useEffect(() => {
    if (!user) { setIsSuperAdmin(false); return; }
    getUserAccessContext(user.id).then((access) => setIsSuperAdmin(access.isSuperAdmin));
  }, [user]);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 50);
  }, []);

  // Load messages when opened
  useEffect(() => {
    if (!open || !user) return;
    setLoadingMsgs(true);
    supabase
      .from("support_messages")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: true })
      .limit(100)
      .then(({ data }) => {
        setMessages((data as SupportMessage[]) || []);
        setLoadingMsgs(false);
        scrollToBottom();
      });
  }, [open, user, scrollToBottom]);

  // Mark team messages as read
  useEffect(() => {
    if (!open || !user || messages.length === 0) return;
    const unread = messages.filter((m) => m.sender_type === "team" && !m.is_read);
    if (unread.length > 0) {
      supabase
        .from("support_messages")
        .update({ is_read: true })
        .eq("user_id", user.id)
        .eq("sender_type", "team")
        .eq("is_read", false)
        .then(() => {
          setMessages((prev) => prev.map((m) => (m.sender_type === "team" ? { ...m, is_read: true } : m)));
          setUnreadCount(0);
        });
    }
  }, [open, user, messages]);

  // Realtime subscription
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel("support-chat")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "support_messages", filter: `user_id=eq.${user.id}` }, (payload) => {
        const newMsg = payload.new as SupportMessage;
        setMessages((prev) => {
          if (prev.some((m) => m.id === newMsg.id)) return prev;
          return [...prev, newMsg];
        });
        if (newMsg.sender_type === "team" && !open) {
          setUnreadCount((c) => c + 1);
        }
        scrollToBottom();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, open, scrollToBottom]);

  // Count unread on mount
  useEffect(() => {
    if (!user) return;
    supabase
      .from("support_messages")
      .select("id", { count: "exact", head: true })
      .eq("user_id", user.id)
      .eq("sender_type", "team")
      .eq("is_read", false)
      .then(({ count }) => setUnreadCount(count || 0));
  }, [user]);

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    if (!user) { openLogin(); return; }
    const now = Date.now();
    if (now - lastSentRef.current < RATE_LIMIT_MS) return;
    lastSentRef.current = now;

    const text = input.trim();
    setInput("");
    setSending(true);

    const { error } = await supabase.from("support_messages").insert({
      user_id: user.id,
      message: text,
      sender_type: "user",
    });

    setSending(false);
    if (error) {
      setInput(text);
    } else {
      scrollToBottom();
    }
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  };

  if (location.pathname !== "/") return null;

  return (
    <>
      {/* Floating button */}
      <AnimatePresence>
      {!open && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            onClick={() => { setOpen(true); if (isSuperAdmin) setAdminMode("conversations"); }}
            className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center"
            aria-label="Ouvrir le chat"
          >
            {isSuperAdmin ? <Shield className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
            {unreadCount > 0 && !isSuperAdmin && (
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-destructive text-destructive-foreground text-xs font-bold flex items-center justify-center">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat window */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 350, damping: 30 }}
            className="fixed bottom-4 right-4 z-50 w-[calc(100vw-2rem)] sm:w-[380px] h-[min(500px,calc(100vh-2rem))] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            {isSuperAdmin && adminMode === "conversations" ? (
              <AdminChatView onClose={() => setOpen(false)} />
            ) : (
              <>
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-primary/5">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                      <MessageCircle className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground">Support KelasAI</p>
                      <div className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                        <span className="text-xs text-muted-foreground">En ligne</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setOpen(false)} className="p-2 rounded-xl hover:bg-muted transition-colors text-muted-foreground" aria-label="Réduire">
                      <Minus className="w-4 h-4" />
                    </button>
                    <button onClick={() => setOpen(false)} className="p-2 rounded-xl hover:bg-muted transition-colors text-muted-foreground" aria-label="Fermer">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Messages */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                  <div className="flex gap-2">
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <MessageCircle className="w-3.5 h-3.5 text-primary" />
                    </div>
                    <div className="bg-muted rounded-2xl rounded-tl-md px-3.5 py-2.5 max-w-[80%]">
                      <p className="text-sm text-foreground">{WELCOME_MSG}</p>
                    </div>
                  </div>

                  {loadingMsgs && (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                  )}

                  {messages.map((msg) => (
                    <div key={msg.id} className={`flex gap-2 ${msg.sender_type === "user" ? "flex-row-reverse" : ""}`}>
                      {msg.sender_type === "team" && (
                        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <MessageCircle className="w-3.5 h-3.5 text-primary" />
                        </div>
                      )}
                      <div
                        className={`rounded-2xl px-3.5 py-2.5 max-w-[80%] ${
                          msg.sender_type === "user"
                            ? "bg-primary text-primary-foreground rounded-tr-md"
                            : "bg-muted text-foreground rounded-tl-md"
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                        <p className={`text-[10px] mt-1 ${msg.sender_type === "user" ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Input */}
                <div className="border-t border-border px-3 py-2.5 bg-card">
                  {!user ? (
                    <button
                      onClick={() => openLogin()}
                      className="w-full py-3 rounded-2xl text-sm font-bold bg-primary text-primary-foreground"
                    >
                      Se connecter pour discuter
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        ref={inputRef}
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Écrire un message..."
                        className="flex-1 bg-muted rounded-2xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none min-h-[44px]"
                        maxLength={1000}
                        autoComplete="off"
                      />
                      <button
                        onClick={handleSend}
                        disabled={!input.trim() || sending}
                        className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40 transition-opacity flex-shrink-0"
                        aria-label="Envoyer"
                      >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Close button overlay for admin mode */}
            {isSuperAdmin && adminMode === "conversations" && (
              <button
                onClick={() => setOpen(false)}
                className="absolute top-3 right-3 p-1.5 rounded-xl hover:bg-muted transition-colors text-muted-foreground z-10"
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SupportChatWidget;
